import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import axios from 'axios';
import { toast } from 'react-toastify';
import VBox from '../../components/layout/VBox';
import HBox from '../../components/layout/HBox';
import Grid, { GridItem } from '../../components/layout/Grid';
import CampoTexto from '../../components/Formulario/CampoTexto';
import CampoNumero from '../../components/Formulario/CampoNumero';
import CampoSeletor from '../../components/Formulario/CampoSeletor';
import CampoCheckbox from '../../components/Formulario/CampoCheckbox';
import Botao from '../../components/Botao';
import { mdiContentSave, mdiLoading } from '@mdi/js';
import CampoData from '@/components/Formulario/CampoData';
import { addMinutes, format, parse } from 'date-fns';

type ConfiguracoesGerais = {
    tempo_limite_fila?: number;
    tempo_limite_inatividade?: number;
    nome_bot?: string;
    modo_mensagens_sensiveis?: 'ocultar' | 'mascarar' | 'exibir';
    chat_interno_habilitado?: boolean;
    ia_aviso_ferramenta_intervalo?: number;
    // Outros campos futuros podem ser adicionados aqui
};

const SecaoGeral = () => {
    const [configuracoes, setConfiguracoes] = useState<ConfiguracoesGerais>({});
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

    const queryClient = useQueryClient();

    // Opções para o campo de mensagens sensíveis
    const opcoesMensagensSensiveis = [
        { id: 'ocultar', label: 'Ocultar completamente' },
        { id: 'mascarar', label: <span>Mascarar (🔒🔒🔒)</span> },
        { id: 'exibir', label: 'Exibir normalmente' },
    ];

    // Query para buscar configurações gerais
    const { isLoading } = useQuery(
        'configuracoes/geral',
        async () => {
            const response = await axios.get('/api/configuracoes/geral', {
                withCredentials: true,
            });
            return response.data.result;
        },
        {
            onSuccess: (data) => {
                setConfiguracoes(data || {});
            },
            onError: () => {
                toast.error('Erro ao carregar configurações gerais');
            },
            refetchOnWindowFocus: false,
        }
    );

    // Mutation para salvar configurações
    const saveMutation = useMutation(
        async (data: ConfiguracoesGerais) => {
            return await axios.put('/api/configuracoes/geral', data, {
                withCredentials: true,
            });
        },
        {
            onSuccess: () => {
                toast.success('Configurações salvas com sucesso');
                queryClient.invalidateQueries('configuracoes/geral');
                setFieldErrors({});
            },
            onError: (error: any) => {
                console.error(error);
                const fieldErrors = error.response?.data?.fieldErrors || {};
                const message = error.response?.data?.msg || 'Erro ao salvar configurações';

                setFieldErrors(fieldErrors);
                toast.error(message);
            },
        }
    );

    const handleSave = () => {
        saveMutation.mutate(configuracoes);
    };

    const handleChange = (field: string, value: any) => {
        setConfiguracoes(prev => ({ ...prev, [field]: value }));
        // Limpar erro do campo quando usuário começar a digitar
        if (fieldErrors[field]) {
            setFieldErrors(prev => ({ ...prev, [field]: '' }));
        }
    };

    // Remover valores padrão - usar os do backend
    // React.useEffect(() => {
    //   if (!isLoading) {
    //     setConfiguracoes({
    //       tempo_limite_fila: 300, // 5 minutos
    //       tempo_limite_inatividade: 1800, // 30 minutos
    //       nome_bot: 'Assistente',
    //       modo_mensagens_sensiveis: 'mascarar',
    //     });
    //   }
    // }, [isLoading]);

    const disableFields = isLoading || saveMutation.isLoading;

    return (
        <VBox gap="24px">
            <HBox>
                <VBox gap="8px">
                    <h3>Configurações Gerais</h3>
                    <p style={{ color: 'var(--tc-color-gray-600)' }}>
                        Configure parâmetros gerais do sistema como tempos limite,
                        comportamentos do bot e exibição de conteúdo.
                    </p>
                </VBox>
                <Botao
                    variant="primary"
                    icon={saveMutation.isLoading ? mdiLoading : mdiContentSave}
                    onClick={handleSave}
                    disabled={disableFields}
                >
                    {saveMutation.isLoading ? 'Salvando...' : 'Salvar'}
                </Botao>
            </HBox>

            <h4>Atendimento</h4>

            <Grid>
                <GridItem sm={12} md={6}>
                    <CampoData
                        label="Tempo limite em fila"
                        type="time"
                        step="1"
                        value={
                            configuracoes.tempo_limite_fila
                                ? addMinutes(new Date(0), configuracoes.tempo_limite_fila).toISOString().substring(11, 16)
                                : ''
                        }
                        onChange={(v: string) => {
                            const parsed = parse(v, 'HH:mm:ss', new Date(0));
                            const minutes = parsed.getHours() * 60 + parsed.getMinutes();
                            handleChange('tempo_limite_fila', minutes);
                        }}
                        error={fieldErrors.tempo_limite_fila}
                        disabled={disableFields}
                    />
                    <small style={{ color: 'var(--tc-color-gray-600)', marginTop: '4px', display: 'block' }}>
                        Tempo máximo que um cliente fica aguardando na fila antes de ser direcionado.
                    </small>
                </GridItem>

                <GridItem sm={12} md={6}>
                    <CampoData
                        label="Tempo limite de inatividade"
                        type="time"
                        step="1"
                        value={
                            configuracoes.tempo_limite_inatividade
                                ? addMinutes(new Date(0), configuracoes.tempo_limite_inatividade).toISOString().substring(11, 16)
                                : ''
                        }
                        onChange={(v: string) => {
                            const parsed = parse(v, 'HH:mm:ss', new Date(0));
                            const minutes = parsed.getHours() * 60 + parsed.getMinutes();
                            handleChange('tempo_limite_inatividade', minutes);
                        }}
                        error={fieldErrors.tempo_limite_inatividade}
                        disabled={disableFields}
                    />
                    <small style={{ color: 'var(--tc-color-gray-600)', marginTop: '4px', display: 'block' }}>
                        Tempo máximo de inatividade antes de encerrar automaticamente o atendimento.
                    </small>
                </GridItem>

                <GridItem sm={12} md={6}>
                    <CampoTexto
                        label="Nome do Bot"
                        value={configuracoes.nome_bot || ''}
                        onChange={(v) => handleChange('nome_bot', v)}
                        placeholder="Assistente"
                        error={fieldErrors.nome_bot}
                        maxLength={50}
                        disabled={disableFields}
                        prepend={<span style={{ color: 'var(--tc-color-gray-600)' }}>Bot </span>}
                    />
                    <small style={{ color: 'var(--tc-color-gray-600)', marginTop: '4px', display: 'block' }}>
                        Nome que será exibido para o bot/assistente virtual.
                    </small>
                </GridItem>

                <GridItem sm={12} md={6}>
                    <CampoSeletor
                        label="Modo de exibição de mensagens sensíveis"
                        options={opcoesMensagensSensiveis}
                        optionLabel="label"
                        optionValue="id"
                        value={configuracoes.modo_mensagens_sensiveis || 'mascarar'}
                        onChange={(v) => handleChange('modo_mensagens_sensiveis', v)}
                        error={fieldErrors.modo_mensagens_sensiveis}
                        disabled={disableFields}
                    />
                    <small style={{ color: 'var(--tc-color-gray-600)', marginTop: '4px', display: 'block' }}>
                        Como exibir mensagens com conteúdo sensível (CPF, cartões, etc.).
                    </small>
                </GridItem>
            </Grid>

            <h4>Atendente IA</h4>

            <Grid>
                <GridItem sm={12} md={6}>
                    <CampoNumero
                        label="Avisos de consulta a ferramentas/integrações"
                        value={String(configuracoes.ia_aviso_ferramenta_intervalo ?? 30)}
                        onChange={(v: string) =>
                            handleChange('ia_aviso_ferramenta_intervalo', Number(v || 0))
                        }
                        somentePositivos
                        decimais={0}
                        append={<span style={{ color: 'var(--tc-color-gray-600)' }}>segundos</span>}
                        error={fieldErrors.ia_aviso_ferramenta_intervalo}
                        disabled={disableFields}
                    />
                    <small style={{ color: 'var(--tc-color-gray-600)', marginTop: '4px', display: 'block' }}>
                        Quando uma consulta a ferramenta ou integração passa de 2 segundos, o
                        atendente de IA avisa o cliente ("só um momento, consultando o
                        catálogo…"). Depois de avisar, fica em silêncio por este tempo, para
                        que uma sequência de consultas não vire uma sequência de avisos. Use 0
                        para desativar — o registro das consultas continua visível para os
                        atendentes na conversa.
                    </small>
                </GridItem>
            </Grid>

            <h4>Funcionalidades</h4>

            <Grid>
                <GridItem sm={12}>
                    <CampoCheckbox
                        value={configuracoes.chat_interno_habilitado ?? true}
                        onChange={(v) => handleChange('chat_interno_habilitado', v)}
                        disabled={disableFields}
                    >
                        Habilitar Chat Interno
                    </CampoCheckbox>
                    <small style={{ color: 'var(--tc-color-gray-600)', marginTop: '4px', display: 'block', marginLeft: '28px' }}>
                        Permite que os colaboradores conversem entre si através do chat interno da plataforma.
                    </small>
                </GridItem>
            </Grid>

            <HBox gap="12px" style={{ justifyContent: 'flex-end' }}>
                <Botao
                    variant="primary"
                    icon={saveMutation.isLoading ? mdiLoading : mdiContentSave}
                    onClick={handleSave}
                    disabled={disableFields}
                >
                    {saveMutation.isLoading ? 'Salvando...' : 'Salvar'}
                </Botao>
            </HBox>
        </VBox>
    );
};

export default SecaoGeral;